/* eslint-disable ember/classic-decorator-hooks */
import EmberObject from '@ember/object';

import FilterConstants from '../../../../filter-constants';
import ActualFilterAmplitude from './actual-filter-amplitude';
import NoiseGroup from './noise-group';
import IdealOpAmp from './ideal-op-amp';
import * as CommonUtils from '../../../../utils/common';
import HighPassSallenKeyCircuit from './circuit';
import * as CircuitUtils from '../../../utils/circuit';
import * as SallenKeyHighPassCircuitLimitsUtils from './circuit-limits-utils';
import * as ComponentFactory from '../../../components/components-factory';
import { calculateFilterComponents } from '@ebiz/aditools-filter';
import { set } from '@ember/object';

// Encapsulates the component creation of sallen key highpass
// A circuit is created from the stage properties stageFp, stageQ, and gain, and the optimization;
export default class SallenKeyHighPassCircuit extends EmberObject {
  // Inputs
  stageFp;
  stageQ;
  gain;
  optimization;
  algorithm;

  // Process output - something used internally but on other classes
  supportedComponents;
  actualFilterAmplitudeModel;
  noiseGroupModel;
  idealOpAmpModel;
  circuitModel;
  idealOpAmpValuePaths;
  componentLimits;
  frequency;
  sizing;

  // Figures output - used internally and also as output in the figures
  needReferenceStage = true;
  c1;
  c2;
  r1;
  r2;
  r3;
  r4;
  masterCapacitor;
  allowGainChange;
  opAmp;

  // For cGov params of calculateFilterComponents - aditools-filter
  c1IdealValue;

  init() {
    super.init();

    if (
      CommonUtils.isNullOrUndefined(this.stageFp) ||
      CommonUtils.isNullOrUndefined(this.stageQ) ||
      CommonUtils.isNullOrUndefined(this.gain) ||
      CommonUtils.isNullOrUndefined(this.optimization) ||
      CommonUtils.isNullOrUndefined(this.algorithm)
    ) {
      throw 'stageFp, stageQ, gain, optimization, and algorithm should not be null';
    }

    this.actualFilterAmplitudeModel = ActualFilterAmplitude;
    this.noiseGroupModel = NoiseGroup;
    this.idealOpAmpModel = IdealOpAmp;
    this.circuitModel = HighPassSallenKeyCircuit;
    this.supportedComponents = ['r1', 'r2', 'r3', 'r4', 'c1', 'c2'];
    this.idealOpAmpValuePaths = ['r1', 'r2', 'r3', 'r4', 'c1', 'c2'];

    if (!CommonUtils.isNullOrUndefined(this.initialCapacitorValue)) {
      this.c1IdealValue = this._computeC1IdealValue(this.initialCapacitorValue);
    } else {
      this.c1IdealValue = this._computeC1IdealValue();
    }
    const idealValues = this._computeIdealValues();
    const capacitorLimits = SallenKeyHighPassCircuitLimitsUtils.computeCapacitorLimitMultipliers(
      idealValues.c1IdealValue,
      idealValues.c2IdealValue,
      idealValues.r1IdealValue,
      idealValues.r2IdealValue,
    );
    const gainResistorLimits = SallenKeyHighPassCircuitLimitsUtils.computeGainResistorLimitMultipliers(
      idealValues.r3IdealValue,
      idealValues.r4IdealValue,
    );
    this._computeComponents(idealValues, capacitorLimits, gainResistorLimits);
  }

  _computeIdealValues() {
    const rGain = this._computeR3AndR4().r3 + this._computeR3AndR4().r4;
    const c1IdealValue = this.c1IdealValue;
    const params = {
      type: this.algorithm,
      implementation: FilterConstants.implementationType.sallenKey,
      fp: this.stageFp,
      gain: this.gain,
      q: this.stageQ,
      cGov: c1IdealValue,
      rGainOrCCross: rGain,
    };
    const filterComponents = calculateFilterComponents(
      params.type,
      params.implementation,
      params.fp,
      params.gain,
      params.q,
      params.cGov,
      params.rGainOrCCross,
    );

    return {
      c1IdealValue: filterComponents.c1,
      c2IdealValue: filterComponents.c2,
      r1IdealValue: filterComponents.r1,
      r2IdealValue: filterComponents.r2,
      r3IdealValue: filterComponents.r3,
      r4IdealValue: filterComponents.r4,
    };
  }

  _computeR3AndR4() {
    const r3PlusR4 = this.optimization.determineStartingR3R4Value(this.stageFp);
    return CircuitUtils.computeR3AndR4(r3PlusR4, this.gain);
  }

  _computeC1IdealValue(value) {
    return value ? value : CircuitUtils.computeMasterCapacitorValue(this.optimization, this.stageFp);
  }

  _computeComponents(idealValues, capacitorLimits, gainResistorLimits) {
    let r3NominalValue = ComponentFactory.createR3(idealValues.r3IdealValue, null, null).toCommercialComponent().value;
    this.allowGainChange = CircuitUtils.getGainChangeSupportStatus(idealValues.r3IdealValue, idealValues.r4IdealValue);
    this.sizing = this._getSizing(idealValues, capacitorLimits);
    this.c1 = ComponentFactory.createC1(
      idealValues.c1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      this.rcRatio,
    );
    this.masterCapacitor = this.c1;
    let rcRatioInverse = CircuitUtils.getInverseOfMasterCapacitorRatio(this.c1);
    this.r1 = ComponentFactory.createR1(
      idealValues.r1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      rcRatioInverse,
    );
    this.r2 = ComponentFactory.createR2(
      idealValues.r2IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      rcRatioInverse,
    );
    this.c2 = ComponentFactory.createC2(
      idealValues.c2IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      rcRatioInverse,
    );
    this.r3 = ComponentFactory.createR3(
      r3NominalValue,
      gainResistorLimits.resistorMinimumLimitMultiplier,
      gainResistorLimits.resistorMaximumLimitMultiplier,
    );
    this.r4 = this._getR4(idealValues, gainResistorLimits);
    this.frequency = 1 / (2 * Math.PI * Math.sqrt(this.r1.value * this.r2.value * this.c1.value * this.c2.value));

    this.idealValues = idealValues;
    this.capacitorLimits = capacitorLimits;
    this.gainResistorLimits = gainResistorLimits;
  }

  _getSizing(idealValues, capacitorLimits) {
    let r1 = ComponentFactory.createR1(
      idealValues.r1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
    );
    return CircuitUtils.getResistorSize(r1);
  }

  // R4 may have an ideal value but it is recomputed here based on gain and R3
  // since it should adjust depending on the current value of R3.
  // (If r3 is not present or idealValue is 0 or INFINITY, we use the idealValue)
  // R3 may change due to the user changing the slider position or r3 adjusting to its nominal value.
  // R3 may also change when changing the e-series in the tolerance tab.
  _getR4(idealValues, gainResistorLimits) {
    let result = null;
    if (idealValues.r3IdealValue !== 0 && idealValues.r3IdealValue !== FilterConstants.Limits.INFINITY)
      result = ComponentFactory.createR4FromR3AndGain(
        this.r3.value,
        this.gain,
        gainResistorLimits.resistorMinimumLimitMultiplier,
        gainResistorLimits.resistorMaximumLimitMultiplier,
      );
    else
      result = ComponentFactory.createR4(
        idealValues.r4IdealValue,
        gainResistorLimits.resistorMinimumLimitMultiplier,
        gainResistorLimits.resistorMaximumLimitMultiplier,
      );

    return result;
  }

  changeGainValues(newR3Value) {
    if (this.allowGainChange) {
      this.r3 = this.r3.setValue(newR3Value);
      this.updateR4();
    }
  }

  updateR4() {
    set(this, 'r4', this._getR4(this.idealValues, this.gainResistorLimits));
  }

  changeInitialCapacitor(value) {
    this.c1IdealValue = this._computeC1IdealValue(value);
    const idealValues = this._computeIdealValues();
    const capacitorLimits = SallenKeyHighPassCircuitLimitsUtils.computeCapacitorLimitMultipliers(
      idealValues.c1IdealValue,
      idealValues.c2IdealValue,
      idealValues.r1IdealValue,
      idealValues.r2IdealValue,
    );
    const gainResistorLimits = SallenKeyHighPassCircuitLimitsUtils.computeGainResistorLimitMultipliers(
      idealValues.r3IdealValue,
      idealValues.r4IdealValue,
    );
    this._computeComponents(idealValues, capacitorLimits, gainResistorLimits);
  }
}
